import { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { ArrowDown, ArrowUp, Pencil, Plus, Trash2 } from 'lucide-react';
import {
  AdminPage,
  Button,
  ErrorState,
  Panel,
  Skeleton,
  StatusBadge,
  useUndo,
} from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { errorMessage } from '@/lib/errors';
import {
  useCourseEditor,
  useCourseMutations,
  useLearningLists,
  type AdminLesson,
  type LessonDraft,
} from './api';
import { QuizBuilder } from './QuizBuilder';
import { ACCEPT, fileTooBig, needsFile, sizeLabel, uploadLessonFile } from './upload';

const FIELD =
  'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

/** בונה הלומדה: פרטים, שיעורים וחידון (PRD 5.6.1). */
export function CourseEditorPage() {
  const undo = useUndo();
  const { courseId = '' } = useParams();
  const canManage = usePerm('learning.manage');
  const editor = useCourseEditor(courseId);
  const lists = useLearningLists();
  const mutations = useCourseMutations(courseId);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Partial<AdminLesson> | null>(null);

  if (!canManage) return <Navigate to="/admin" replace />;
  if (editor.isPending) return <Skeleton className="m-8 h-64" />;
  if (editor.isError) return <ErrorState onRetry={() => void editor.refetch()} />;
  if (!editor.data?.course) return <Navigate to="/admin/learning" replace />;

  const { course, lessons, quiz } = editor.data;

  const run = (action: () => Promise<unknown>) => {
    setError(null);
    void action()
      .then(() => void editor.refetch())
      .catch((caught) => setError(errorMessage(caught)));
  };

  const textType = lists.data?.types.find((t) => t.renderer_key === 'text');

  return (
    <AdminPage
      title={course.title}
      subtitle="פרטי הלומדה, השיעורים והחידון"
      backTo="/admin/learning"
      width="narrow"
      actions={
        course.is_published ? (
          <StatusBadge tone="good">מפורסמת</StatusBadge>
        ) : (
          <StatusBadge tone="warn">טיוטה</StatusBadge>
        )
      }
    >
      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      <Panel title="פרטי הלומדה">
        <DetailsForm
          course={course}
          categories={lists.data?.categories ?? []}
          saving={mutations.updateCourse.isPending}
          onSave={(patch) => run(() => mutations.updateCourse.mutateAsync(patch))}
        />
      </Panel>

      <Panel
        title={`שיעורים (${lessons.length})`}
        actions={
          !editing && (
            <Button
              onClick={() =>
                setEditing({
                  title: '',
                  body: '',
                  content_type_id: textType?.id,
                  sort_order: lessons.length + 1,
                })
              }
            >
              <Plus size={20} strokeWidth={1.75} aria-hidden="true" />
              שיעור חדש
            </Button>
          )
        }
      >
        <div className="grid gap-3">
          {lessons.length === 0 && !editing && (
            <p className="m-0 text-body-sm text-muted">אין עדיין שיעורים בלומדה הזו.</p>
          )}

          {lessons.filter((lesson) => !undo.isPending(lesson.id)).map((lesson, i) => (
            <div
              key={lesson.id}
              className="flex flex-wrap items-center gap-2 rounded-md border border-line p-3"
            >
              <span className="w-6 shrink-0 tabular text-muted">{i + 1}</span>
              <span className="grow text-body">{lesson.title}</span>
              <span className="text-label text-muted">
                {lists.data?.types.find((t) => t.id === lesson.content_type_id)?.name}
              </span>
              <Button
                variant="ghost"
                aria-label="העלאה בסדר"
                disabled={i === 0}
                onClick={() => run(() => mutations.swapLessons.mutateAsync([lesson, lessons[i - 1]!]))}
              >
                <ArrowUp size={18} strokeWidth={1.75} aria-hidden="true" />
              </Button>
              <Button
                variant="ghost"
                aria-label="הורדה בסדר"
                disabled={i === lessons.length - 1}
                onClick={() => run(() => mutations.swapLessons.mutateAsync([lesson, lessons[i + 1]!]))}
              >
                <ArrowDown size={18} strokeWidth={1.75} aria-hidden="true" />
              </Button>
              <Button variant="ghost" aria-label="עריכה" onClick={() => setEditing(lesson)}>
                <Pencil size={18} strokeWidth={1.75} aria-hidden="true" />
              </Button>
              <Button
                variant="ghost"
                aria-label="מחיקה"
                onClick={() =>
                  undo.remove({
                    id: lesson.id,
                    message: `"${lesson.title}" נמחק`,
                    perform: () => mutations.removeLesson.mutateAsync(lesson.id),
                  })
                }
              >
                <Trash2 size={18} strokeWidth={1.75} aria-hidden="true" />
              </Button>
            </div>
          ))}

          {editing && (
            <LessonForm
              courseId={courseId}
              lesson={editing}
              types={lists.data?.types ?? []}
              onCancel={() => setEditing(null)}
              onSave={(draft) =>
                run(async () => {
                  await mutations.saveLesson.mutateAsync(draft);
                  setEditing(null);
                })
              }
            />
          )}
        </div>
      </Panel>

      <QuizBuilder courseId={courseId} quiz={quiz} onChanged={() => void editor.refetch()} />
    </AdminPage>
  );
}

/**
 * פרטי הלומדה.
 *
 * רכיב נפרד, כדי שה state שלו יתחיל מהערכים של הלומדה: הוא נטען רק
 * אחרי שהיא הגיעה. select נשלט עם ערך שעוד אין לו אופציה מציג ריק
 * לרגע, ומתיישר לבד כשהקטגוריות מגיעות.
 */
function DetailsForm({
  course,
  categories,
  saving,
  onSave,
}: {
  course: {
    title: string;
    description: string | null;
    category_id: string | null;
    points_reward: number;
    is_published: boolean;
  };
  categories: { id: string; name: string }[];
  saving: boolean;
  onSave: (patch: {
    title: string;
    description: string | null;
    category_id: string | null;
    points_reward: number;
    is_published: boolean;
  }) => void;
}) {
  const [title, setTitle] = useState(course.title);
  const [description, setDescription] = useState(course.description ?? '');
  const [categoryId, setCategoryId] = useState(course.category_id ?? '');
  const [points, setPoints] = useState(String(course.points_reward));
  const [published, setPublished] = useState(course.is_published);

  return (
    <form
      className="grid gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        onSave({
          title: title.trim(),
          description: description.trim() || null,
          category_id: categoryId || null,
          points_reward: Number(points) || 0,
          is_published: published,
        });
      }}
    >
      <Field label="שם הלומדה" htmlFor="title">
        <input
          id="title"
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          className={FIELD}
        />
      </Field>

      <Field label="תיאור קצר" htmlFor="description">
        <textarea
          id="description"
          rows={2}
          value={description}
          onChange={(event) => setDescription(event.target.value)}
          className={`${FIELD} py-2`}
        />
      </Field>

      <div className="grid gap-3 sm:grid-cols-2">
        <Field label="קטגוריה" htmlFor="category">
          <select
            id="category"
            value={categoryId}
            onChange={(event) => setCategoryId(event.target.value)}
            className={FIELD}
          >
            <option value="">בלי קטגוריה</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.name}
              </option>
            ))}
          </select>
        </Field>

        <Field label="נקודות על השלמה" htmlFor="points">
          <input
            id="points"
            type="number"
            min={0}
            value={points}
            onChange={(event) => setPoints(event.target.value)}
            className={FIELD}
          />
        </Field>
      </div>

      <label className="flex items-center gap-2 text-body">
        <input
          type="checkbox"
          checked={published}
          onChange={(event) => setPublished(event.target.checked)}
          className="size-5"
        />
        מפורסמת לעובדים
      </label>
      <p className="m-0 text-body-sm text-muted">
        לומדה בטיוטה לא מופיעה אצל העובדים, גם אם שויכה.
      </p>

      <div>
        <Button variant="primary" type="submit" disabled={saving}>
          שמירת הפרטים
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid gap-1">
      <label htmlFor={htmlFor} className="text-body-sm text-muted">
        {label}
      </label>
      {children}
    </div>
  );
}

function LessonForm({
  courseId,
  lesson,
  types,
  onSave,
  onCancel,
}: {
  courseId: string;
  lesson: Partial<AdminLesson>;
  types: { id: string; name: string; renderer_key: string }[];
  onSave: (draft: LessonDraft) => void;
  onCancel: () => void;
}) {
  const [typeId, setTypeId] = useState(lesson.content_type_id ?? types[0]?.id ?? '');
  const kind = types.find((t) => t.id === typeId)?.renderer_key ?? 'text';
  const [mediaPath, setMediaPath] = useState(lesson.media_path ?? null);
  const [busy, setBusy] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);

  /**
   * הקובץ עולה מיד בבחירה ולא בשמירה, כי העלאה של מצגת לוקחת זמן
   * וכפתור שמירה שנתקע לחצי דקה נראה שבור.
   */
  const pickFile = (file: File) => {
    setFileError(null);
    if (fileTooBig(file)) {
      setFileError(`הקובץ ${sizeLabel(file.size)}, והמקסימום הוא 100 מגה.`);
      return;
    }
    setBusy(true);
    uploadLessonFile(courseId, file)
      .then(setMediaPath)
      .catch((caught) => setFileError(errorMessage(caught)))
      .finally(() => setBusy(false));
  };

  return (
    <form
      className="grid gap-3 rounded-md border border-olive bg-olive-soft p-3"
      onSubmit={(event) => {
        event.preventDefault();
        const form = new FormData(event.currentTarget);
        const title = String(form.get('title') ?? '').trim();
        if (!title) return;
        if (needsFile(kind) && !mediaPath) {
          setFileError('צריך להעלות קובץ לשיעור מהסוג הזה.');
          return;
        }
        onSave({
          id: lesson.id,
          title,
          content_type_id: typeId,
          body: String(form.get('body') ?? '').trim() || null,
          external_url: String(form.get('url') ?? '').trim() || null,
          // שיעור שהוחלף לטקסט או לקישור לא גורר איתו קובץ ישן
          media_path: needsFile(kind) ? mediaPath : null,
          sort_order: lesson.sort_order ?? 1,
        });
      }}
    >
      <Field label="שם השיעור" htmlFor="lesson-title">
        <input
          id="lesson-title"
          name="title"
          defaultValue={lesson.title ?? ''}
          className={FIELD}
        />
      </Field>

      <Field label="סוג התוכן" htmlFor="lesson-type">
        <select
          id="lesson-type"
          value={typeId}
          onChange={(event) => setTypeId(event.target.value)}
          className={FIELD}
        >
          {types.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </select>
      </Field>

      {kind === 'link' && (
        <Field label="כתובת החומר" htmlFor="lesson-url">
          <input
            id="lesson-url"
            name="url"
            type="url"
            dir="ltr"
            defaultValue={lesson.external_url ?? ''}
            className={FIELD}
          />
        </Field>
      )}

      {needsFile(kind) && (
        <Field label={kind === 'slides' ? 'קובץ המצגת' : 'הקובץ'} htmlFor="lesson-file">
          <div className="grid gap-1">
            <input
              id="lesson-file"
              type="file"
              accept={ACCEPT[kind]}
              disabled={busy}
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) pickFile(file);
              }}
              className="text-body-sm"
            />
            {kind === 'slides' && (
              <p className="m-0 text-label text-muted">
                מצגת נשמרת כ PDF. בפאוורפוינט: קובץ, שמירה בשם, ולבחור PDF. העובדים יראו
                שקופית אחר שקופית בתוך השיעור.
              </p>
            )}
            {busy && <p className="m-0 text-body-sm">מעלה, אל תסגור/י את החלון…</p>}
            {!busy && mediaPath && (
              <p className="m-0 text-body-sm text-good">
                קובץ נטען. העלאת קובץ חדש תחליף אותו.
              </p>
            )}
            {fileError && (
              <p role="alert" className="m-0 text-body-sm text-bad">
                {fileError}
              </p>
            )}
          </div>
        </Field>
      )}

      <Field label={kind === 'text' ? 'תוכן השיעור' : 'הסבר קצר'} htmlFor="lesson-body">
        <textarea
          id="lesson-body"
          name="body"
          rows={kind === 'text' ? 8 : 2}
          defaultValue={lesson.body ?? ''}
          className={`${FIELD} py-2`}
        />
      </Field>

      <div className="flex flex-wrap gap-2">
        <Button variant="primary" type="submit">
          שמירת השיעור
        </Button>
        <Button onClick={onCancel}>ביטול</Button>
      </div>
    </form>
  );
}
